import { useCallback, useEffect, useRef, type RefObject } from 'react';

const SCROLL_LOCK_CLASS = 'srv-welcome-dialog-open';

export function useWelcomeDialogs(rootRef: RefObject<HTMLDivElement | null>) {
    const returnFocus = useRef(new WeakMap<HTMLDialogElement, HTMLElement>());
    const ownsScrollLock = useRef(false);

    const getDialogs = useCallback(
        () =>
            Array.from(
                rootRef.current?.querySelectorAll<HTMLDialogElement>(
                    'dialog',
                ) ?? [],
            ),
        [rootRef],
    );

    const syncScrollLock = useCallback(() => {
        if (typeof document === 'undefined') return;

        if (getDialogs().some((dialog) => dialog.open)) {
            if (
                !document.documentElement.classList.contains(SCROLL_LOCK_CLASS)
            ) {
                document.documentElement.classList.add(SCROLL_LOCK_CLASS);
                ownsScrollLock.current = true;
            }
        } else if (ownsScrollLock.current) {
            document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
            ownsScrollLock.current = false;
        }
    }, [getDialogs]);

    const openDialog = useCallback(
        (id: string, trigger: HTMLElement) => {
            const dialogs = getDialogs();
            const dialog = dialogs.find((candidate) => candidate.id === id);
            if (!dialog?.isConnected || dialog.open) return;

            const previous = trigger.closest('dialog');
            const previousOwned =
                previous && dialogs.includes(previous) ? previous : null;
            const origin =
                (previousOwned && returnFocus.current.get(previousOwned)) ||
                trigger;

            returnFocus.current.set(dialog, origin);
            for (const candidate of dialogs) {
                if (candidate.open) candidate.close();
            }
            dialog.showModal();
            syncScrollLock();
        },
        [getDialogs, syncScrollLock],
    );

    const closeDialog = useCallback(
        (id?: string) => {
            for (const dialog of getDialogs()) {
                if (dialog.open && (!id || dialog.id === id)) dialog.close();
            }
            syncScrollLock();
        },
        [getDialogs, syncScrollLock],
    );

    useEffect(() => {
        const dialogs = getDialogs();
        const handleClose = (event: Event) => {
            const dialog = event.currentTarget as HTMLDialogElement;
            if (dialog.open) return;
            const origin = returnFocus.current.get(dialog);
            returnFocus.current.delete(dialog);
            syncScrollLock();

            if (
                !getDialogs().some((candidate) => candidate.open) &&
                origin?.isConnected
            ) {
                origin.focus({ preventScroll: true });
            }
        };
        const handleBackdropClick = (event: MouseEvent) => {
            const dialog = event.currentTarget as HTMLDialogElement;
            if (event.target !== dialog) return;

            const bounds = dialog.getBoundingClientRect();
            if (
                event.clientX < bounds.left ||
                event.clientX > bounds.right ||
                event.clientY < bounds.top ||
                event.clientY > bounds.bottom
            ) {
                dialog.close();
            }
        };

        for (const dialog of dialogs) {
            // Native Escape dismissal also emits close, preserving the same
            // scroll and focus restoration as the explicit close buttons.
            dialog.addEventListener('close', handleClose);
            dialog.addEventListener('click', handleBackdropClick);
        }
        syncScrollLock();

        return () => {
            for (const dialog of dialogs) {
                dialog.removeEventListener('close', handleClose);
                dialog.removeEventListener('click', handleBackdropClick);
                if (dialog.open) dialog.close();
            }
            if (ownsScrollLock.current) {
                document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
                ownsScrollLock.current = false;
            }
            returnFocus.current = new WeakMap();
        };
    }, [getDialogs, syncScrollLock]);

    return { openDialog, closeDialog };
}
